// Shortlist & journal: everything you saved, by day, with live change since adding and your notes.
import { Link } from '@/lib/nav';
import { useSearchParams } from '@/lib/nav';
import { useMemo } from 'react';
import { DownloadIcon, StarIcon, Trash2Icon } from 'lucide-react';
import { NSE, NSE_NAMES } from '@planb/core';
import { fmtPct } from '@/charts/board';
import { exportCsv, since, useJournal } from '@/lib/client/use-journal';
import { usePageTools } from '@/lib/desk/tools';
import { todayIso, useIndexStrip, useMarket } from '@/lib/client/use-market';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AppHeader, Page } from './app-header';
import { carry, dateSeed, indexById, stockPath } from './common';
import { NoteField, sinceClass } from './journal';
import { Initials, SideTag } from './market-ui';

const fmtPx = NSE.formatPrice;

export function ShortlistApp() {
  const params = useSearchParams();
  const seed = Number(params.get('seed')) || dateSeed();
  const index = indexById(params.get('index'))?.id ?? 'FNO';
  // Every stock (not one index), so any shortlisted symbol gets a current price.
  const { session, status, mode, live, version, user, role } = useMarket({ index: '', seed });
  const strip = useIndexStrip(session, live, version);
  const journal = useJournal();
  const date = session?.date || todayIso();
  const query = carry({ index, live, seed });

  const ltp = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of session?.stocks ?? []) if (s.bars.length) m.set(s.symbol, s.bars[s.bars.length - 1].c);
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, version]);

  const days = [...new Set(journal.entries.map((e) => e.date))].sort().reverse();
  // The desk's view of this page: the shortlisted stocks, why, and how they've done since.
  usePageTools({
    read_page: () => ({
      page: 'shortlist', today: date, entries: journal.entries.length,
      shortlist: [...journal.entries].sort((x, y) => (x.date + x.time < y.date + y.time ? 1 : -1)).slice(0, 30).map((e) => {
        const now = e.date === date ? ltp.get(e.symbol) : undefined;
        return {
          date: e.date, time: e.time, symbol: e.symbol, sector: e.sector, shortlistedAs: e.side === 'bull' ? 'bullish' : 'bearish',
          priceAtAdd: e.priceAtAdd, changePctAtAdd: +e.pctAtAdd.toFixed(2), why: e.note || null, review: e.review || null,
          moveSinceAddedPct: now != null ? +since(e, now).chg.toFixed(2) : null,
        };
      }),
      note: 'The user\'s own shortlist with their reasons ("why") and reviews. moveSinceAddedPct is today\'s only. ',
    }),
  });

  return (
    <>
      <AppHeader section="shortlist" mode={mode} status={status} user={user} role={role} query={query} strip={strip} />
      <Page>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-[26px] font-semibold">Shortlist &amp; journal</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">Saved to your MoneyHQ account. Write down why, then review after the close.</p>
          </div>
          <Button variant="outline" className="h-9 rounded-full px-4" disabled={!journal.entries.length} onClick={() => exportCsv(journal.entries)}>
            <DownloadIcon /> Export CSV
          </Button>
        </div>
        {journal.error && <p className="mt-4 text-sm text-bear" role="alert">{journal.error}</p>}

        {!journal.entries.length && (
          <Card className="mt-6 items-center px-6 py-12 text-center">
            <span className="grid size-12 place-items-center rounded-full bg-brand-soft text-primary"><StarIcon className="size-5" /></span>
            <h2 className="text-lg font-semibold">Nothing shortlisted yet</h2>
            <p className="max-w-md text-sm text-muted-foreground">
              Open any stock and press <b className="text-ink">Add to shortlist</b>. Writing down <em>why</em> you picked a stock, then
              checking after the close, is how you learn which reasons actually work.
            </p>
            <Button asChild className="mt-2 rounded-full px-5"><Link href={`/movers${query}`}>Browse movers</Link></Button>
          </Card>
        )}

        {days.map((d) => {
          const list = journal.entries.filter((e) => e.date === d);
          const isToday = d === date;
          const moves = isToday ? list.filter((e) => ltp.has(e.symbol)).map((e) => since(e, ltp.get(e.symbol)!)) : [];
          return (
            <section key={d} className="mt-8">
              <h2 className="mb-3 text-[15px] font-semibold">
                {d}{isToday ? ' · today' : ''}
                <span className="ml-2 font-sans text-[13px] font-normal text-muted-foreground">
                  {list.length} stock{list.length === 1 ? '' : 's'}
                  {moves.length ? ` · ${moves.filter((x) => x.good).length} of ${moves.length} moving your way` : ''}
                </span>
              </h2>
              <div className="grid gap-3">
                {list.map((e) => {
                  const now = isToday ? ltp.get(e.symbol) : undefined;
                  const s = now !== undefined ? since(e, now) : null;
                  return (
                    <Card key={e.id} className={cn('gap-4 border-l-4 px-5 py-4', e.side === 'bull' ? 'border-l-bull' : 'border-l-bear')}>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                        <Initials symbol={e.symbol} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <Link href={stockPath(e.symbol, query)} className="font-heading text-base font-semibold text-ink hover:text-brand-strong">{e.symbol}</Link>
                            <SideTag bull={e.side === 'bull'} />
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {NSE_NAMES[e.symbol] ? `${NSE_NAMES[e.symbol]} · ` : ''}{e.sector} · added {e.time} at {NSE.currencySymbol}{fmtPx(e.priceAtAdd)} ({fmtPct(e.pctAtAdd)}) · score {e.score} · {e.withSector ? 'with sector' : 'moving alone'}
                          </div>
                        </div>
                        <span className="flex-1" />
                        {s && now !== undefined && (
                          <div className="text-right">
                            <div className="num text-sm text-ink">{NSE.currencySymbol}{fmtPx(now)}</div>
                            <div className={cn('num text-xs', sinceClass(s.cls))}>{fmtPct(s.chg)} since</div>
                          </div>
                        )}
                        <Button variant="ghost" size="icon-sm" aria-label={`Remove ${e.symbol}`} onClick={() => journal.remove(e.id)}><Trash2Icon /></Button>
                      </div>
                      <div className="grid gap-3.5 sm:grid-cols-2">
                        <NoteField id={`why-${e.id}`} label="Why" value={e.note} placeholder="Why did you pick it?" onSave={(note) => journal.update(e.id, { note })} />
                        <NoteField id={`review-${e.id}`} label="Review" value={e.review} placeholder="After the close: did it follow through? What would you do differently?"
                          onSave={(review) => journal.update(e.id, { review })} />
                      </div>
                    </Card>
                  );
                })}
              </div>
            </section>
          );
        })}
      </Page>
    </>
  );
}
